import { HttpErrorResponse } from '@angular/common/http';
import { Component, Injector, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
    CompaniesProvider,
    onCreateCompanySuccessfully,
    onUpdateCompanyFailed,
    onUpdateCompanySuccessfully,
} from '@modeso/aumico-lib-companies-fe';
import {
    AumBreadcrumbComponentItem,
    AumicoCoreProvider,
    GICSCodesUtil,
    IPageHeader,
    NotificationType,
} from '@modeso/aumico-lib-core-fe';

import { selectAllClosingWorkflows, WorkflowsActions, WorkflowsState } from '@modeso/aumico-lib-cpmm-fe';
import { WorkflowArea } from '@modeso/aumico-lib-cpmm-fe/enums/workflow-area.enum';
import { IFilterOptions, SettingsProvider } from '@modeso/aumico-lib-settings-fe';
import { ICompanyDTO } from '@modeso/types__aumico-lib-companies-be';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { ILegalFormDTO } from '@modeso/types__aumico-lib-settings-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import fastDeepEqual from 'fast-deep-equal/es6';
import * as countries from 'i18n-iso-countries';
import { cloneDeep } from 'lodash-es';
import { combineLatest, filter, first, map, Observable, of, Subject, take } from 'rxjs';
import { getCompanyLogo } from '../../../../../../aumico-lib-companies-fe/src/lib/actions/companies.actions';
import { getUserById } from '../../../../../../aumico-lib-users-fe/src/lib/actions/organization.actions';
import { CPMMState } from '../../../../../../aumico-lib-users-fe/src/lib/enums/cpmm-state.enum';
import { GetUserDto, OrganizationProvider } from '../../../../../../aumico-lib-users-fe/src/public-api';
import { environment } from '../../../../environments/environment';
import { Paths } from '../../../enums/paths.enum';
import { RouteLabels } from '../../../enums/routeLabels.enum';
import { BasePageComponent } from '../../../pages/base.page';
import { CountriesMapper } from '../../../shared/mappers/countries.mapper';
import { CountriesService } from '../../../shared/services/countries.service';
import { localizationKeys } from '../../../utils/localizationKeys';
import { FX_BETA_ORGANIZATION_IDS } from '../../../utils/fx-beta-organizations';

@UntilDestroy()
@Component({
    selector: 'app-page-edit-company.page',
    templateUrl: 'add-edit-company.page.html',
    styleUrls: ['add-edit-company.page.scss'],
})
export class AddOrEditCompanyPage extends BasePageComponent implements OnInit {
    countries: INameId[];
    shortLegalForms$: Observable<INameId[]>;
    countryOptions: INameId[];
    industryOptions: INameId[] = [];
    readonly currentLanguage = this.getCurrentLanguageWithFallBack();
    readonly defaultLanguage = environment.defaultLanguage;
    private createdCompany: ICompanyDTO;
    originalLegalForms: ILegalFormDTO[] = [];
    activeCompany: ICompanyDTO;
    companyId: string;
    private isProceedButtonClicked = false;
    private isError = false;
    proceedButtonEvent$ = new Subject<void>();
    saveCompany = new Subject<void>();
    protected defaultCountry: INameId | undefined;
    editMode: boolean;
    organizationUsers$: Observable<INameId[]>;
    protected uploadedFile: File;
    protected logoHasChanged = false;
    readonly breadcrumbItems: AumBreadcrumbComponentItem[] = [
        {
            displayName: RouteLabels.COMPANIES_MAIN_NAVIGATION,
            routerLink: '../',
        },
    ];
    readonly pageHeader: IPageHeader = {
        isLocalizedKey: true,
        value: RouteLabels.COMPANIES_MAIN_NAVIGATION,
    };

    assignedUser$: Observable<GetUserDto>;
    clerkUser$: Observable<GetUserDto>;

    /**
     * Functional Currency (REV-21512) — only an org admin may activate it, since it commits the
     * organisation to a recurring charge with a minimum term. Lives here rather than on the
     * profile page so the CREATE route (this page) offers it too; the backend enforces the rule.
     * While FX is in beta the organisation must additionally be on the pilot list. Assigned in
     * `ngOnInit` rather than inline: the organisation provider is a parameter property of this
     * class and is not assigned yet while field initializers run.
     */
    canManageFx$: Observable<boolean>;

    organizationHasCpmmActive: boolean;
    organizationHasMandateAndTaskManagementEnabled: boolean;
    workflows: INameId[];

    constructor(
        injector: Injector,
        dialog: MatDialog,
        private countriesMapper: CountriesMapper,
        private countriesService: CountriesService,
        private settingsProvider: SettingsProvider,
        private coreProvider: AumicoCoreProvider,
        protected readonly companiesProvider: CompaniesProvider,
        private readonly organizationsProvider: OrganizationProvider,
        private workflowsStore: Store<WorkflowsState>,
    ) {
        super(injector, dialog);
    }

    override ngOnInit(): void {
        this.canManageFx$ = combineLatest([
            this.authProvider.getIsAdmin$(),
            this.organizationsProvider.getCurrentOrganization$(),
        ]).pipe(
            map(
                ([isOrgAdmin, organization]) =>
                    isOrgAdmin === true && FX_BETA_ORGANIZATION_IDS.includes(organization?.id ?? ''),
            ),
        );
        this.companiesProvider.resetErrors();
        super.ngOnInit();
        this.handleCountries();
        this.organizationsProvider.dispatchGetUsers();
        // The FX beta gate reads the current organisation; the selector does not load it on
        // demand, so a deep link into this page would otherwise leave the control hidden.
        this.organizationsProvider.dispatchGetCurrentOrganization();
        // this.workflowOptions$ = this.workflowProvider.getAllWorkflows$().pipe(
        //     filter((workflows) => workflows != null),
        //     map((workflows) => workflows.map((workflow) => ({ id: workflow.id, name: workflow.name }))),
        // );
        this.workflowsStore
            .select(selectAllClosingWorkflows)
            .pipe(untilDestroyed(this))
            .subscribe((workflows) => {
                this.workflows = workflows.map((workflow) => ({
                    id: workflow.workflowId,
                    name: workflow.name,
                }));
            });

        this.organizationsProvider
            .getCurrentOrganization$()
            .pipe(
                filter((organization) => organization != null),
                untilDestroyed(this),
            )
            .subscribe((organization) => {
                this.organizationHasCpmmActive =
                    (organization.cpmmActivationDetails &&
                        organization.cpmmActivationDetails.activationState === CPMMState.ACTIVE) ??
                    false;
                this.organizationHasMandateAndTaskManagementEnabled = organization.isMandateAndTaskManagementEnabled ?? false;

                if (this.organizationHasCpmmActive) {
                    this.workflowsStore.dispatch(
                        WorkflowsActions.loadWorkflows({ filter: { area: WorkflowArea.CLOSING_PROCESS } }),
                    );
                }
            });

        this.organizationUsers$ = this.organizationsProvider
            .getPossibleAssignees()
            .pipe(
                map((users) => {
                    const userOptions = users.map((user) => ({
                        id: user.id,
                        name: `${user.firstName} ${user.lastName}`,
                        isMe: user.isMe
                    }));

                    // Add current user to the top of the list
                    const index = userOptions.findIndex((user) => user.isMe === true);
                    if (index > -1) {
                        const [item] = userOptions.splice(index, 1); // Remove the item
                        userOptions.unshift(item); // Add it to the beginning
                    }

                    return userOptions;
                })
            );

        this.assignedUser$ = this.actionsListener$.pipe(
            ofType(getUserById.success),
            map(({ payload }) => payload),
            filter((user) => user.id === this.activeCompany?.assignee),
        );

        this.clerkUser$ = this.actionsListener$.pipe(
            ofType(getUserById.success),
            map(({ payload }) => payload),
            filter((user) => user.id === this.activeCompany?.clerk),
        );

        this.companyId = this.activatedRoute.snapshot.params['companyId']
            ?? this.activatedRoute.parent?.snapshot.params['companyId'];
        if (this.companyId == null) {
            this.editMode = false;
            this.countriesService.getCountryForCompanyWithNoCountry$().subscribe((initialCountry) => {
                const filterOptions: IFilterOptions = {
                    countryISO31661Alpha2Code: initialCountry,
                };

                this.defaultCountry = this.countries.find((country) => country.id === initialCountry);
                this.settingsProvider.dispatchGetAllLegalForm(filterOptions);
            });
            this.actionsListener$
                .pipe(ofType(onCreateCompanySuccessfully), untilDestroyed(this))
                .subscribe((action) => this.onSaveSuccessfully(action.payload.company));
        } else {
            this.editMode = true;

            this.companiesProvider
                .getSelectedCompanyById$()
                .pipe(untilDestroyed(this))
                .subscribe((savedCompany: ICompanyDTO) => {
                    //company deleted
                    if (savedCompany == null) {
                        this.router.navigate([
                            `./${this.currentLanguage}/${Paths.HOME}/${Paths.COMPANIES}/${Paths.OVERVIEW}`,
                        ]);
                        return;
                    }

                    if (savedCompany.logo) {
                        this.companiesProvider.downloadCompanyLogo(savedCompany.logo.uuid);
                    }

                    if (savedCompany.assignee != null) {
                        this.organizationsProvider.dispatchGetUser(savedCompany.assignee);
                    }
                    if (savedCompany.clerk != null) {
                        this.organizationsProvider.dispatchGetUser(savedCompany.clerk);
                    }

                    this.createdCompany = cloneDeep(savedCompany);
                    this.activeCompany = savedCompany;
                });

            this.actionsListener$
                .pipe(ofType(onUpdateCompanySuccessfully), untilDestroyed(this))
                .subscribe((action) => this.onSaveSuccessfully(action.payload));
            this.actionsListener$
                .pipe(ofType(onUpdateCompanyFailed), ofType(onUpdateCompanyFailed), untilDestroyed(this))
                .subscribe(() => this.onSaveFailed());

            this.actionsListener$
                .pipe(
                    ofType(getCompanyLogo.downloadsuccess),
                    map((action) => action.payload),
                    take(1),
                )
                .subscribe((payload) => {
                    this.uploadedFile = new File([payload], this.activeCompany?.logo.name || `logo.${payload.type}`, {
                        type: payload.type,
                    });
                });

            this.countriesService
                .getCountryToExistingCompany$(this.companyId)
                .pipe(untilDestroyed(this))
                .subscribe((initialCountry) => {
                    const filterOptions: IFilterOptions = {
                        countryISO31661Alpha2Code: initialCountry,
                    };
                    this.defaultCountry = this.countries.find((country) => country.id === initialCountry);
                    this.settingsProvider.dispatchGetAllLegalForm(filterOptions);
                });
            this.companiesProvider.dispatchCompanyById(this.companyId);
        }

        this.shortLegalForms$ = this.settingsProvider
            .allLegalForm$()
            .pipe(
                map((legalForms) => {
                    this.originalLegalForms = cloneDeep(legalForms);

                    return legalForms.map((legalForm) => ({
                        id: legalForm._id,
                        name:
                            legalForm.text?.[this.currentLanguage] ??
                            legalForm.text?.[this.defaultLanguage] ??
                            legalForm.name,
                    }));
                }),
            );

        this.industryOptions = GICSCodesUtil.getAllGICSCodesAndSubindustryNames();
    }

    private handleCountries(): void {
        const retrievedCountriesByUSerLanguage: countries.LocalizedCountryNames<{ select: 'official' }> =
            this.countriesService.getCountriesByLanguage(this.currentLanguage);
        this.countries = this.countriesMapper.mapCountries(retrievedCountriesByUSerLanguage);
    }

    setCreatedCompany(company: ICompanyDTO): void {
        this.createdCompany = company;
    }

    enableProceedButton(isFormValid: boolean): void {
        if (isFormValid) {
            this.disableProceedButton = false;
        } else {
            this.disableProceedButton = true;
        }
    }

    saveButtonClicked = (): void => {
        this.proceedButtonEvent$.next();
        this.isProceedButtonClicked = true;
    };

    protected override isDataChanged(): Observable<boolean> {
        if ((this.isProceedButtonClicked && !this.isError) || this.createdCompany == null) {
            return of(false);
        } else if (this.isError) {
            return of(true);
        } else if (this.logoHasChanged) {
            return of(true);
        } else if (this.activeCompany != null && this.createdCompany != null) {
            this.createdCompany._id = this.activeCompany._id;
            type CompanyWithWorkflow = ICompanyDTO & { workflow?: string };
            const companyToBeSaved: CompanyWithWorkflow = cloneDeep(this.createdCompany);

            if (companyToBeSaved.workflow) {
                companyToBeSaved.workflowId = companyToBeSaved.workflow;
            }
            function removeProperties<T>(obj: T, propertiesToIgnore: string[]): T {
                const objCopy = cloneDeep(obj);
                propertiesToIgnore.forEach((prop) => {
                    delete objCopy[prop as keyof typeof objCopy];
                });
                return objCopy;
            }
            // `fx` is managed by its own endpoints and is not part of this form, so it
            // only ever exists on the server-side company — comparing it would report
            // unsaved changes forever once FX is activated.
            const propertiesToIgnore = ['assignedOn', 'active', '_id', 'logo', 'workflow', 'fx'];
            const modifiedActiveCompany = removeProperties(this.activeCompany, propertiesToIgnore);
            const modifiedCompanyToBeSaved = removeProperties(companyToBeSaved, propertiesToIgnore);

            if (modifiedActiveCompany.assignee == '') {
                modifiedActiveCompany.assignee = undefined;
            }
            if (!modifiedCompanyToBeSaved.workflowId) {
                modifiedCompanyToBeSaved.workflowId = null;
            }

            // Normalize legalform to _id only — the embedded backend object and the
            // settings-API object share the same _id but may have different shapes.
            const normalizeLegalForm = (company: typeof modifiedActiveCompany) => ({
                ...company,
                legalform: (company.legalform as any)?._id ?? null,
            });
            const isActiveCompanyEqualCompanyToBeSaved = fastDeepEqual(
                normalizeLegalForm(modifiedActiveCompany),
                normalizeLegalForm(modifiedCompanyToBeSaved),
            );
            return of(!isActiveCompanyEqualCompanyToBeSaved);
        } else if (this.createdCompany != null) {
            return of(true);
        }
        return of(true);
    }

    private onSaveSuccessfully(savedCompany: ICompanyDTO): void {
        this.savedSuccessfully.next(true);
        this.handleActiveCompany(savedCompany);
        this.fireSaveNotification();
        if (!this.editMode) {
            this.router.navigate([
                `./${this.currentLanguage}/${Paths.HOME}/${Paths.COMPANIES}/${savedCompany._id}/${Paths.PROJECT}`,
            ]);
        }
    }

    override save(): Observable<void> {
        this.logoHasChanged = false;
        this.saveCompany.next();
        return of(void 0);
    }

    private fireSaveNotification(): void {
        const successDescriptionLocalizationKey = localizationKeys.aumico_companies_changes_saved_successfully_dialog;
        const successTitleLocalizationKey = localizationKeys.aumico_reports_notification_success_title;
        this.coreProvider.dispatchAddNotification(
            successTitleLocalizationKey,
            successDescriptionLocalizationKey,
            NotificationType.SUCCESS,
        );
    }

    public handleErrors(error: HttpErrorResponse): void {
        if (error != null) {
            this.isError = true;
            this.savedSuccessfully.next(false);
        }
    }
    handleActiveCompany(activeCompany: ICompanyDTO) {
        this.activeCompany = activeCompany;
    }
    private onSaveFailed(): void {
        this.savedSuccessfully.next(false);
        this.fireErrorMessage();
    }

    onCountrySelectionChange(countryCode: string): void {
        let filterOptions: IFilterOptions;
        if (countryCode == undefined) {
            this.countriesService
                .getCountryForCompanyWithNoCountry$()
                .pipe(first())
                .subscribe((initialCountry) => {
                    const filterOptions: IFilterOptions = {
                        countryISO31661Alpha2Code: initialCountry,
                    };

                    this.settingsProvider.dispatchGetAllLegalForm(filterOptions);
                });
        } else {
            filterOptions = {
                countryISO31661Alpha2Code: countryCode,
            };
            this.settingsProvider.dispatchGetAllLegalForm(filterOptions);
        }
    }

    saveAndStay = (): void => {
        this.save();
    };
}
